import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { Loading } from 'components/Loading/Loading';
import { useUser } from 'core/stores/hooks/useUser';
import { POST_SSO_REDIRECT_KEY } from 'services/auth/utils/ssoRedirect';
import { organizationAtom } from 'services/organization/atom';
import { useUserFetcher } from 'services/user/hooks';
import { embeddedCandidatesPath } from '../constants/atsEmbedLinks';
import { useResolveAtsOrganization } from '../hooks/useResolveAtsOrganization';
import { startAtsEmbed } from '../utils/atsEmbedContext';
import {
  AtsEntryParams,
  parseAtsEntryParams
} from '../utils/parseAtsEntryParams';
import { AtsMessage } from './AtsMessage';

const clearPostSsoRedirect = () => {
  try {
    sessionStorage.removeItem(POST_SSO_REDIRECT_KEY);
  } catch {
    // Storage can be blocked in a third-party frame; the redirect must not depend on it.
  }
};

const AtsEntryForInstance: React.FC<{ entryParams: AtsEntryParams }> = ({
  entryParams
}) => {
  const resolution = useResolveAtsOrganization(
    entryParams.instanceId,
    entryParams.organizationId
  );
  const user = useUser();
  const { changeOrganization } = useUserFetcher();
  const currentOrganization = useRecoilValue(organizationAtom);
  const navigate = useNavigate();
  const hasLeft = useRef(false);

  const resolvedOrganization =
    resolution.status === 'resolved' ? resolution.organization : null;

  useEffect(() => {
    if (!resolvedOrganization || hasLeft.current) return;

    if (currentOrganization?.id !== resolvedOrganization.id) {
      changeOrganization(
        user.id,
        resolvedOrganization,
        user.organizationGroupRole
      );
      return;
    }

    hasLeft.current = true;
    clearPostSsoRedirect();
    startAtsEmbed({
      instanceId: entryParams.instanceId,
      organizationId: resolvedOrganization.id,
      jobId: entryParams.jobId
    });

    navigate(embeddedCandidatesPath, { replace: true });
  }, [
    resolvedOrganization,
    currentOrganization?.id,
    changeOrganization,
    user.id,
    user.organizationGroupRole,
    entryParams,
    navigate
  ]);

  if (resolution.status === 'notConnected') {
    return (
      <AtsMessage
        title="This Bullhorn account is not connected to a Popp account you can access"
        body="Ask your Popp administrator to connect it, or to give you access."
      />
    );
  }
  if (resolution.status === 'unavailable') {
    return (
      <AtsMessage
        title="The connected Popp account is not available"
        body="It may be hidden. Ask your Popp administrator to check it."
      />
    );
  }
  if (resolution.status === 'failed') {
    return (
      <AtsMessage
        title="We could not load your Popp account"
        body="Check your connection and try again."
        actionLabel="Try again"
        onAction={() => window.location.reload()}
      />
    );
  }
  return <Loading />;
};

export const AtsEntryPage: React.FC = () => {
  const location = useLocation();
  const entryParams = useMemo(
    () => parseAtsEntryParams(location.search),
    [location.search]
  );

  if (!entryParams) {
    return (
      <AtsMessage
        title="This link is missing its Bullhorn account"
        body="Open Popp again from Bullhorn."
      />
    );
  }

  return <AtsEntryForInstance entryParams={entryParams} />;
};
